import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShieldCheck, Download, Infinity, CreditCard, ChevronDown, Star, ArrowRight, AlertTriangle, BookOpen, Users, TrendingUp, Globe, Briefcase, FileText, HelpCircle, Lock, Zap, FileDown } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { SocialProofPopup } from './SocialProofPopup';
// Image URLs
const IMAGES = {
  MAIN: 'https://cdn.magicpatterns.com/uploads/2KbaoPFPDArcgcvRWHHdN4/mockups_economia_.png',
  BONUS1: 'https://cdn.magicpatterns.com/uploads/6JxosyVSZjXjLscaapUHyG/bonus_maletin_de_act.png',
  BONUS2: 'https://cdn.magicpatterns.com/uploads/xdCZarQDHvWmR8sHQbcS62/bonus_primeros_auxilios.png',
  BONUS3: 'https://cdn.magicpatterns.com/uploads/7jPddC3JCCs6g7bjXSoGJ3/bonus_planificacion_anual.png',
  UPSELL: 'https://cdn.magicpatterns.com/uploads/9J3MioKiq1fVGdgGA4X9rp/upsell_economia_.png'
};
// Links
const LINKS = {
  MAIN_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554509390144:1',
  UPSELL_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554508833088:1',
  CONTABILIDAD_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554510143808:1',
  PREMIUM_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554510471488:1',
  PROYECTO_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51602555437376:1',
  TEORIA_ORG_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51622247465280:1',
  CONTA_TEORIA_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51622258082112:1'
};
export function SalesPage() {
  const [openCuadernillo, setOpenCuadernillo] = useState<number | null>(null);
  const [selectedPack, setSelectedPack] = useState(2); // 0=economia, 1=conta+gestion, 2=contabilidad, 3=premium
  const packLinks = [LINKS.MAIN_CHECKOUT, LINKS.UPSELL_CHECKOUT, LINKS.CONTABILIDAD_CHECKOUT, LINKS.PREMIUM_CHECKOUT, LINKS.PROYECTO_CHECKOUT, LINKS.TEORIA_ORG_CHECKOUT, LINKS.CONTA_TEORIA_CHECKOUT];
  // Urgency countdown (2h 13m 30s from page load)
  const [urgencyTime, setUrgencyTime] = useState({
    hours: 2,
    minutes: 13,
    seconds: 30
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgencyTime(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        seconds--;
        if (seconds < 0) {
          seconds = 59;
          minutes--;
        }
        if (minutes < 0) {
          minutes = 59;
          hours--;
        }
        if (hours < 0) {
          hours = 0;
          minutes = 0;
          seconds = 0;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="font-sans text-gray-900 bg-white min-h-screen flex flex-col" data-id="element-2">
      <SocialProofPopup data-id="element-3" />

      {/* SECTION 2: HERO */}
      <section className="bg-black py-4 md:py-16 px-3 md:px-4 text-center" data-id="element-4">
        <div className="max-w-4xl mx-auto" data-id="element-5">
          <span className="inline-block border border-[#eab308] text-[#eab308] text-[10px] md:text-sm font-bold uppercase tracking-widest mb-2 md:mb-5 px-3 py-1 rounded-full whitespace-nowrap shadow-[0_0_12px_rgba(234,179,8,0.25)]" data-id="element-6">
            Para docentes de Contabilidad en secundaria
          </span>

          <h1 className="text-3xl md:text-6xl lg:text-7xl font-black text-white mb-1 md:mb-2 leading-tight" data-id="element-7">
            Todo tu año de Contabilidad{' '}
            <span className="text-[#eab308] italic" data-id="element-8">resuelto</span>
          </h1>

          <div onClick={scrollToPricing} className="cursor-pointer hover:opacity-95 transition-opacity my-2 md:my-8" data-id="element-9">
            <img src="https://cdn.magicpatterns.com/uploads/8XZqaHt2an8fhUopQs7Yxe/CONJUNTO_DE_MOCKUPS_contabilidad_(1).png" alt="Kit Completo Contabilidad Lista Mockup" className="w-full max-w-xs md:max-w-sm mx-auto drop-shadow-2xl" data-id="element-10" />
          </div>

          <p className="text-sm md:text-xl text-gray-300 mb-2 md:mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-11">
            Mientras otros docentes todavía están buscando materiales,{' '}
            <span className="text-[#eab308] font-bold" data-id="element-12">
              vos tenés todo el año de Contabilidad resuelto
            </span>
            , planificación, actividades, evaluaciones y ejercicios integrados.
          </p>

          {/* Small badges above CTA */}
          <div className="flex justify-center gap-1.5 md:gap-3 mb-2 md:mb-4 flex-nowrap" data-id="element-13">
            <span className="flex items-center gap-1 border border-gray-600 text-gray-300 text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full whitespace-nowrap" data-id="element-14">
              <FileText className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-15" /> 100% PDF
            </span>
            <span className="flex items-center gap-1 border border-gray-600 text-gray-300 text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full whitespace-nowrap" data-id="element-16">
              <Zap className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-17" /> Acceso inmediato
            </span>
            <span className="flex items-center gap-1 border border-gray-600 text-gray-300 text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full whitespace-nowrap" data-id="element-18">
              🇦🇷 Currículo argentino
            </span>
          </div>

          <button onClick={scrollToPricing} className="inline-flex items-center justify-center bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-sm md:text-xl py-2.5 px-5 md:py-3 md:px-8 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] transform hover:scale-105 transition-all duration-200 mb-2 md:mb-4 whitespace-nowrap" data-id="element-19">
            → QUIERO MI KIT COMPLETO AHORA
          </button>

          {/* Trust icons below CTA */}
          <div className="flex justify-center gap-2 md:gap-4 text-[10px] md:text-xs text-gray-500 font-medium flex-nowrap" data-id="element-20">
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-21">
              <ShieldCheck className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-22" />{' '}
              Garantía 7 días
            </span>
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-23">
              <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-24" /> Pago
              seguro
            </span>
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-25">
              <CreditCard className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-26" />{' '}
              Mercado Pago
            </span>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="bg-[#0a0e1f] py-3 md:py-4 px-3 border-t border-[#1a1f3a]" data-id="element-27">
        <div className="max-w-md mx-auto flex items-center justify-center gap-3" data-id="element-28">
          <div className="flex items-center -space-x-2.5" data-id="element-29">
            {['#3b82f6', '#a855f7', '#22c55e', '#ef4444'].map((color, i) => <div key={i} className="w-8 h-8 md:w-9 md:h-9 rounded-full border-2 border-[#0a0e1f] flex items-center justify-center text-white font-bold text-[10px] md:text-xs" style={{
            backgroundColor: color,
            zIndex: 4 - i
          }} data-id="element-30">
                {['MC', 'LP', 'SR', '+'][i]}
              </div>)}
          </div>
          <div className="flex flex-col" data-id="element-31">
            <p className="text-white text-sm md:text-base font-bold leading-tight" data-id="element-32">
              <span className="text-[#22c55e]" data-id="element-33">+2.400 docentes</span> arrancaron
              el año con el kit
            </p>
            <div className="flex items-center gap-0.5" data-id="element-34">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 md:w-4 md:h-4 fill-[#eab308] text-[#eab308]" data-id="element-35" />)}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: PAIN POINTS */}
      <section className="bg-gray-50 py-3 md:py-16 px-3 border-t border-gray-200" data-id="element-36">
        <div className="max-w-3xl mx-auto" data-id="element-37">
          <h2 className="text-2xl md:text-4xl font-black text-center text-gray-900 mb-2 md:mb-8 uppercase italic leading-tight" data-id="element-38">
            Si sos docente de Contabilidad,{' '}
            <span className="bg-[#eab308] text-white px-2 py-0.5 rounded-md not-italic" data-id="element-39">
              esto te va a sonar familiar…
            </span>
          </h2>

          <div className="space-y-1.5 md:space-y-4 mb-3 md:mb-10" data-id="element-40">
            {/* Pain 1 */}
            <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 md:p-5 border-l-4 border-yellow-400 shadow-sm" data-id="element-41">
              <span className="text-xl md:text-2xl flex-shrink-0" data-id="element-42">📋</span>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed" data-id="element-43">
                <span className="font-bold text-gray-900" data-id="element-44">
                  Armás clases desde cero
                </span>{' '}
                una y otra vez, perdiendo horas valiosas de tu tiempo personal.
              </p>
            </div>
            {/* Pain 2 */}
            <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 md:p-5 border-l-4 border-green-400 shadow-sm" data-id="element-45">
              <span className="text-xl md:text-2xl flex-shrink-0" data-id="element-46">🤢</span>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed" data-id="element-47">
                Terminás una clase y{' '}
                <span className="font-bold text-gray-900" data-id="element-48">
                  ya estás pensando en la siguiente
                </span>
                , sin descanso mental real.
              </p>
            </div>
            {/* Pain 3 */}
            <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 md:p-5 border-l-4 border-orange-400 shadow-sm" data-id="element-49">
              <span className="text-xl md:text-2xl flex-shrink-0" data-id="element-50">😢</span>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed" data-id="element-51">
                Trabajás mucho, pero sentís que{' '}
                <span className="font-bold text-gray-900" data-id="element-52">nunca alcanza</span> y
                el material se agota rápido.
              </p>
            </div>
            {/* Pain 4 */}
            <div className="flex items-start gap-2 bg-white rounded-lg p-2.5 md:p-5 border-l-4 border-red-400 shadow-sm" data-id="element-53">
              <span className="text-xl md:text-2xl flex-shrink-0" data-id="element-54">🏚️</span>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed" data-id="element-55">
                Entrás al aula{' '}
                <span className="font-bold text-gray-900" data-id="element-56">
                  cansado/a antes de empezar
                </span>{' '}
                por el estrés de la planificación.
              </p>
            </div>
          </div>

          {/* Bottom statements */}
          <div className="text-center space-y-1 md:space-y-3" data-id="element-57">
            <p className="text-base md:text-xl text-gray-800 font-bold" data-id="element-58">
              <span className="text-red-500" data-id="element-59">✖</span> No es falta de vocación.{' '}
              <span className="text-red-500" data-id="element-60">✖</span> No es falta de ganas.
            </p>
            <p className="text-lg md:text-2xl font-black text-gray-900" data-id="element-61">
              👉 Es falta de un sistema claro y material listo.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3.5: SOLUTION INTRO */}
      <section className="bg-white py-2 md:py-4 px-3" data-id="element-62">
        <div className="max-w-3xl mx-auto" data-id="element-63">
          <h2 className="text-base md:text-xl font-black text-center text-blue-700 mb-0.5 leading-tight" data-id="element-64">
            Por eso creamos el KIT COMPLETO CONTABILIDAD LISTA
          </h2>
          <p className="text-center text-xs md:text-sm text-gray-500 mb-2 md:mb-3" data-id="element-65">
            Un sistema pensado para dejar de improvisar y entrar al aula con
            todo resuelto.
          </p>

          <div className="grid grid-cols-2 gap-1.5 md:gap-2 mb-2" data-id="element-66">
            <div className="bg-blue-50 rounded-lg p-2 md:p-2.5 border border-blue-100" data-id="element-67">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-68">
                <span className="text-blue-600 text-sm" data-id="element-69">📋</span>
                <h3 className="font-bold text-xs md:text-sm text-gray-900" data-id="element-70">
                  Todos los años organizados
                </h3>
              </div>
              <p className="text-[10px] md:text-xs text-gray-600 leading-snug" data-id="element-71">
                4 cuadernillos listos, estructurados pedagógicamente.
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-2 md:p-2.5 border border-green-100" data-id="element-72">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-73">
                <span className="text-green-600 text-sm" data-id="element-74">✅</span>
                <h3 className="font-bold text-xs md:text-sm text-gray-900" data-id="element-75">
                  Clases listas para aplicar
                </h3>
              </div>
              <p className="text-[10px] md:text-xs text-gray-600 leading-snug" data-id="element-76">
                Actividades probadas en el aula, sin empezar de cero.
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-2 md:p-2.5 border border-purple-100" data-id="element-77">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-78">
                <span className="text-purple-600 text-sm" data-id="element-79">📘</span>
                <h3 className="font-bold text-xs md:text-sm text-gray-900" data-id="element-80">
                  Contenidos claros y prácticos
                </h3>
              </div>
              <p className="text-[10px] md:text-xs text-gray-600 leading-snug" data-id="element-81">
                Teoría paso a paso, ejemplos reales y recursos visuales.
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-2 md:p-2.5 border border-orange-100" data-id="element-82">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-83">
                <span className="text-orange-500 text-sm" data-id="element-84">😌</span>
                <h3 className="font-bold text-xs md:text-sm text-gray-900" data-id="element-85">
                  Más tiempo y tranquilidad
                </h3>
              </div>
              <p className="text-[10px] md:text-xs text-gray-600 leading-snug" data-id="element-86">
                Menos planificación. Más seguridad. Recuperá tus fines de
                semana.
              </p>
            </div>
          </div>

          <p className="text-center text-xs md:text-sm font-bold text-green-600" data-id="element-87">
            ✨ Menos estrés. Más orden. Más disfrute al dar clase.
          </p>
        </div>
      </section>

      {/* SECTION 5: CONTENTS */}
      <section className="bg-white py-4 md:py-20 px-3" data-id="element-88">
        <div className="max-w-4xl mx-auto" data-id="element-89">
          <div className="text-center mb-3 md:mb-12" data-id="element-90">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-91">
              📦 ¿QUÉ INCLUYE EL{' '}
              <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-4" data-id="element-92">
                KIT COMPLETO
              </span>
              ?
            </h2>
            <p className="text-xl text-gray-600" data-id="element-93">
              ✅ Tocá cada cuadernillo para ver su contenido
            </p>
          </div>

          <div className="space-y-1.5 md:space-y-4" data-id="element-94">
            {/* Cuadernillo 1 */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-95">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 0 ? null : 0)} className="w-full flex items-center gap-3 p-3 md:p-8 text-left cursor-pointer" data-id="element-96">
                <BookOpen className="w-6 h-6 md:w-8 md:h-8 text-blue-600 flex-shrink-0" data-id="element-97" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-98">
                  <span className="text-blue-600" data-id="element-99">Cuadernillo 1</span> –
                  Fundamentos de la Contabilidad
                </h3>
                <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 0 ? 'rotate-180' : ''}`} data-id="element-100" />
              </button>
              <AnimatePresence data-id="element-101">
                {openCuadernillo === 0 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-102">
                    <div className="px-3 md:px-8 pb-3 md:pb-8 border-t border-gray-200 pt-3" data-id="element-103">
                      <ul className="space-y-1.5 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-104">
                        <li className="flex items-start gap-2" data-id="element-105">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-106" />{' '}
                          Concepto y objetivos de la contabilidad
                        </li>
                        <li className="flex items-start gap-2" data-id="element-107">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-108" />{' '}
                          Importancia en la vida cotidiana y las organizaciones
                        </li>
                        <li className="flex items-start gap-2" data-id="element-109">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-110" />{' '}
                          Usuarios de la información contable
                        </li>
                        <li className="flex items-start gap-2" data-id="element-111">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-112" />{' '}
                          Tipos de contabilidad
                        </li>
                        <li className="flex items-start gap-2" data-id="element-113">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-114" />{' '}
                          Patrimonio: Activo, Pasivo y Patrimonio Neto
                        </li>
                        <li className="flex items-start gap-2" data-id="element-115">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-116" />{' '}
                          Ecuación y variaciones patrimoniales
                        </li>
                        <li className="flex items-start gap-2" data-id="element-117">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-118" />{' '}
                          La empresa y su clasificación
                        </li>
                        <li className="flex items-start gap-2" data-id="element-119">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-120" />{' '}
                          Introducción al lenguaje contable y hechos económicos
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Cuadernillo 2 */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-121">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 1 ? null : 1)} className="w-full flex items-center gap-3 p-3 md:p-8 text-left cursor-pointer" data-id="element-122">
                <FileText className="w-6 h-6 md:w-8 md:h-8 text-red-600 flex-shrink-0" data-id="element-123" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-124">
                  <span className="text-red-600" data-id="element-125">Cuadernillo 2</span> –
                  Documentación y Registración Contable
                </h3>
                <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 1 ? 'rotate-180' : ''}`} data-id="element-126" />
              </button>
              <AnimatePresence data-id="element-127">
                {openCuadernillo === 1 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-128">
                    <div className="px-3 md:px-8 pb-3 md:pb-8 border-t border-gray-200 pt-3" data-id="element-129">
                      <ul className="space-y-1.5 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-130">
                        <li className="flex items-start gap-2" data-id="element-131">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-132" />{' '}
                          Documentación comercial y su función
                        </li>
                        <li className="flex items-start gap-2" data-id="element-133">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-134" />{' '}
                          Facturas, recibos, remitos
                        </li>
                        <li className="flex items-start gap-2" data-id="element-135">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-136" />{' '}
                          Notas de débito y crédito
                        </li>
                        <li className="flex items-start gap-2" data-id="element-137">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-138" />{' '}
                          Comprobantes electrónicos
                        </li>
                        <li className="flex items-start gap-2" data-id="element-139">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-140" />{' '}
                          Hechos económicos y su impacto patrimonial
                        </li>
                        <li className="flex items-start gap-2" data-id="element-141">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-142" />{' '}
                          Cuentas contables y plan de cuentas
                        </li>
                        <li className="flex items-start gap-2" data-id="element-143">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-144" />{' '}
                          Asientos contables
                        </li>
                        <li className="flex items-start gap-2" data-id="element-145">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-146" />{' '}
                          Libro Diario y Libro Mayor
                        </li>
                        <li className="flex items-start gap-2" data-id="element-147">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-148" />{' '}
                          Registración paso a paso con ejercicios integrales
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Cuadernillo 3 */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-149">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 2 ? null : 2)} className="w-full flex items-center gap-3 p-3 md:p-8 text-left cursor-pointer" data-id="element-150">
                <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-green-600 flex-shrink-0" data-id="element-151" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-152">
                  <span className="text-green-600" data-id="element-153">Cuadernillo 3</span> –
                  Resultados y Estados Contables
                </h3>
                <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 2 ? 'rotate-180' : ''}`} data-id="element-154" />
              </button>
              <AnimatePresence data-id="element-155">
                {openCuadernillo === 2 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-156">
                    <div className="px-3 md:px-8 pb-3 md:pb-8 border-t border-gray-200 pt-3" data-id="element-157">
                      <ul className="space-y-1.5 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-158">
                        <li className="flex items-start gap-2" data-id="element-159">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-160" />{' '}
                          Ingresos, costos y gastos
                        </li>
                        <li className="flex items-start gap-2" data-id="element-161">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-162" />{' '}
                          Resultado del ejercicio
                        </li>
                        <li className="flex items-start gap-2" data-id="element-163">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-164" />{' '}
                          Estado de Resultados
                        </li>
                        <li className="flex items-start gap-2" data-id="element-165">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-166" />{' '}
                          Armado e interpretación básica
                        </li>
                        <li className="flex items-start gap-2" data-id="element-167">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-168" />{' '}
                          Estado de Situación Patrimonial
                        </li>
                        <li className="flex items-start gap-2" data-id="element-169">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-170" />{' '}
                          Activo, Pasivo y Patrimonio Neto
                        </li>
                        <li className="flex items-start gap-2" data-id="element-171">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-172" />{' '}
                          Análisis simple de estados contables
                        </li>
                        <li className="flex items-start gap-2" data-id="element-173">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-174" />{' '}
                          Casos prácticos y ejercicios integradores
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Cuadernillo 4 */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-175">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 3 ? null : 3)} className="w-full flex items-center gap-3 p-3 md:p-8 text-left cursor-pointer" data-id="element-176">
                <Briefcase className="w-6 h-6 md:w-8 md:h-8 text-orange-600 flex-shrink-0" data-id="element-177" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-178">
                  <span className="text-orange-600" data-id="element-179">Cuadernillo 4</span> –
                  Contabilidad Aplicada y Educación Financiera
                </h3>
                <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 3 ? 'rotate-180' : ''}`} data-id="element-180" />
              </button>
              <AnimatePresence data-id="element-181">
                {openCuadernillo === 3 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-182">
                    <div className="px-3 md:px-8 pb-3 md:pb-8 border-t border-gray-200 pt-3" data-id="element-183">
                      <ul className="space-y-1.5 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-184">
                        <li className="flex items-start gap-2" data-id="element-185">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-186" />{' '}
                          Contabilidad en la vida real
                        </li>
                        <li className="flex items-start gap-2" data-id="element-187">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-188" />{' '}
                          Emprendimientos y PYMES
                        </li>
                        <li className="flex items-start gap-2" data-id="element-189">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-190" />{' '}
                          Empresa escolar y simulaciones
                        </li>
                        <li className="flex items-start gap-2" data-id="element-191">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-192" />{' '}
                          Registro simplificado
                        </li>
                        <li className="flex items-start gap-2" data-id="element-193">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-194" />{' '}
                          Educación financiera
                        </li>
                        <li className="flex items-start gap-2" data-id="element-195">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-196" />{' '}
                          Presupuesto, ahorro y consumo responsable
                        </li>
                        <li className="flex items-start gap-2" data-id="element-197">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-198" />{' '}
                          Aplicación práctica para todos los años
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom banner */}
        </div>
      </section>

      {/* SECTION 8: BONUSES */}
      <section className="py-4 md:py-20 px-3" style={{
      background: 'linear-gradient(180deg, #080c1a 0%, #0d1229 100%)'
    }} data-id="element-199">
        <div className="max-w-5xl mx-auto" data-id="element-200">
          {/* Header */}
          <div className="text-center mb-3 md:mb-10" data-id="element-201">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-2 leading-tight" data-id="element-202">
              <span className="text-[#eab308]" data-id="element-203">BONUS</span> exclusivos incluidos
              con tu compra
            </h2>
            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto mb-3 md:mb-8" data-id="element-204">
              Para que tengas respaldo profesional, evaluaciones sólidas y
              listas de herramientas para implementar, incluimos 3 bonos
              estratégicos:
            </p>

            {/* Red CTA Banner */}
            <div className="inline-flex items-center gap-2 bg-red-600 text-white font-black text-base md:text-xl px-4 md:px-10 py-2 md:py-4 rounded-lg shadow-[0_0_30px_rgba(220,38,38,0.4)] mb-3 md:mb-10" data-id="element-205">
              <span className="text-xl md:text-2xl" data-id="element-206">🎁</span>
              ¡GRATIS POR EL DÍA DE HOY!
            </div>
          </div>

          {/* Bonus Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-5 mb-3 md:mb-10" data-id="element-207">
            {/* Bonus 1 */}
            <div className="relative bg-[#111633] rounded-lg border border-[#1e2a5a] overflow-hidden p-2.5 md:p-5 flex flex-col" data-id="element-208">
              <span className="absolute top-1 right-2 text-[50px] md:text-[80px] font-black text-white/[0.04] leading-none select-none pointer-events-none" data-id="element-209">
                01
              </span>
              <span className="inline-block bg-red-600 text-white text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 self-start" data-id="element-210">
                BONO #01
              </span>
              <h3 className="text-lg md:text-xl font-bold text-white mb-1.5" data-id="element-211">
                🩺 Guía Docente de Primeros Auxilios
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-212">
                Explica de forma clara y práctica cómo actuar ante emergencias
                escolares: desmayos, golpes, cortes o crisis nerviosas.
              </p>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10" data-id="element-213">
                <span className="text-gray-500 text-sm line-through" data-id="element-214">
                  Valor $15.000
                </span>
                <span className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-215">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="relative bg-[#111633] rounded-lg border border-[#1e2a5a] overflow-hidden p-2.5 md:p-5 flex flex-col" data-id="element-216">
              <span className="absolute top-1 right-2 text-[50px] md:text-[80px] font-black text-white/[0.04] leading-none select-none pointer-events-none" data-id="element-217">
                02
              </span>
              <span className="inline-block bg-yellow-500 text-black text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 self-start" data-id="element-218">
                BONO #02
              </span>
              <h3 className="text-lg md:text-xl font-bold text-white mb-1.5" data-id="element-219">
                📘 Guía Maestra de Planificación Anual
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-220">
                Incluye listas de planificaciones, organizadas por temas y
                niveles. Todo listo para que organices tu año sin estrés.
              </p>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10" data-id="element-221">
                <span className="text-gray-500 text-sm line-through" data-id="element-222">
                  Valor $12.000
                </span>
                <span className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-223">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus 3 */}
            <div className="relative bg-[#111633] rounded-lg border border-[#1e2a5a] overflow-hidden p-2.5 md:p-5 flex flex-col" data-id="element-224">
              <span className="absolute top-1 right-2 text-[50px] md:text-[80px] font-black text-white/[0.04] leading-none select-none pointer-events-none" data-id="element-225">
                03
              </span>
              <span className="inline-block bg-red-600 text-white text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 self-start" data-id="element-226">
                BONO #03
              </span>
              <h3 className="text-lg md:text-xl font-bold text-white mb-1.5" data-id="element-227">
                ✨ Actividades para tus clases listas
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-228">
                Clases listas. 50 listas de actividades para que pongas en
                práctica en el aula. Recurso práctico e inmediato.
              </p>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10" data-id="element-229">
                <span className="text-gray-500 text-sm line-through" data-id="element-230">
                  Valor $10.000
                </span>
                <span className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-231">
                  GRATIS
                </span>
              </div>
            </div>
          </div>

          {/* Total Value Summary Box */}
          <div className="max-w-md mx-auto bg-[#111633] border border-[#1e2a5a] rounded-2xl p-3 md:p-8 text-center" data-id="element-232">
            <p className="text-gray-400 text-base mb-1" data-id="element-233">
              Valor total de los 3 bonus:
            </p>
            <p className="text-white text-2xl md:text-3xl font-black line-through decoration-red-500 decoration-2 mb-1 md:mb-4" data-id="element-234">
              $37.000
            </p>
            <p className="text-gray-400 text-base mb-1" data-id="element-235">
              Hoy te los llevás por:
            </p>
            <p className="text-[#eab308] text-4xl md:text-6xl font-black mb-1 md:mb-4" data-id="element-236">
              $0
            </p>
            <p className="text-[#eab308] font-bold text-sm md:text-base" data-id="element-237">
              ✨ Incluidos GRATIS con tu compra de hoy ✨
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 8.5: URGENCY */}
      <section className="py-4 md:py-20 px-3" style={{
      background: 'linear-gradient(180deg, #0d1229 0%, #0a0e1a 100%)'
    }} data-id="element-238">
        <div className="max-w-3xl mx-auto text-center" data-id="element-239">
          {/* Title */}
          <h2 className="text-2xl md:text-4xl font-black text-yellow-400 mb-2 md:mb-10 uppercase tracking-wide" data-id="element-240">
            El precio de lanzamiento termina en
          </h2>

          {/* Countdown boxes */}
          <div className="flex items-center justify-center gap-1.5 md:gap-5 mb-3 md:mb-10" data-id="element-241">
            <div className="flex flex-col items-center" data-id="element-242">
              <div className="bg-red-700 border-2 border-red-500 rounded-xl w-14 h-14 md:w-28 md:h-28 flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.4)]" data-id="element-243">
                <span className="text-white text-3xl md:text-6xl font-black" data-id="element-244">
                  {String(urgencyTime.hours).padStart(2, '0')}
                </span>
              </div>
              <span className="text-gray-500 text-xs md:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-245">
                Horas
              </span>
            </div>
            <span className="text-red-500 text-3xl md:text-6xl font-black -mt-5" data-id="element-246">
              :
            </span>
            <div className="flex flex-col items-center" data-id="element-247">
              <div className="bg-red-700 border-2 border-red-500 rounded-xl w-14 h-14 md:w-28 md:h-28 flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.4)]" data-id="element-248">
                <span className="text-white text-3xl md:text-6xl font-black" data-id="element-249">
                  {String(urgencyTime.minutes).padStart(2, '0')}
                </span>
              </div>
              <span className="text-gray-500 text-xs md:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-250">
                Minutos
              </span>
            </div>
            <span className="text-red-500 text-3xl md:text-6xl font-black -mt-5" data-id="element-251">
              :
            </span>
            <div className="flex flex-col items-center" data-id="element-252">
              <div className="bg-red-700 border-2 border-red-500 rounded-xl w-14 h-14 md:w-28 md:h-28 flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.4)]" data-id="element-253">
                <span className="text-white text-3xl md:text-6xl font-black" data-id="element-254">
                  {String(urgencyTime.seconds).padStart(2, '0')}
                </span>
              </div>
              <span className="text-gray-500 text-xs md:text-sm font-bold uppercase tracking-wider mt-1" data-id="element-255">
                Segundos
              </span>
            </div>
          </div>

          {/* Info badges */}
          <div className="flex flex-wrap justify-center gap-1.5 md:gap-4 mb-3 md:mb-10" data-id="element-256">
            <span className="flex items-center gap-2 bg-[#111633] border border-[#1e2a5a] text-gray-300 text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg" data-id="element-257">
              🔥 Últimas unidades disponibles
            </span>
            <span className="flex items-center gap-2 bg-[#111633] border border-[#1e2a5a] text-gray-300 text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg" data-id="element-258">
              📢 Oferta de Lanzamiento
            </span>
            <span className="flex items-center gap-2 bg-[#111633] border border-[#1e2a5a] text-gray-300 text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg" data-id="element-259">
              📈 Precio sube al terminar
            </span>
          </div>

          {/* Progress bar */}
          <div className="max-w-lg mx-auto mb-3 md:mb-10" data-id="element-260">
            <div className="relative w-full h-4 md:h-5 bg-gray-800 rounded-full overflow-hidden border border-gray-700" data-id="element-261">
              <div className="h-full rounded-full" style={{
              width: '72%',
              background: 'linear-gradient(90deg, #f97316 0%, #ef4444 60%, #dc2626 100%)'
            }} data-id="element-262" />
            </div>
            <p className="text-gray-400 text-sm font-bold mt-2 uppercase tracking-wider" data-id="element-263">
              72 % de cupos ocupados
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 9: PRICING */}
      <section id="pricing" className="py-4 md:py-20 px-3" style={{
      background: 'linear-gradient(180deg, #080c1a 0%, #0d1229 50%, #080c1a 100%)'
    }} data-id="element-264">
        <div className="max-w-5xl mx-auto" data-id="element-265">
          {/* Title */}
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-3 md:mb-12 italic" data-id="element-266">
            Elegí tu{' '}
            <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-8" data-id="element-267">
              Pack Completo
            </span>
          </h2>

          {/* Pack Cards Grid */}
          <div className="grid grid-cols-2 gap-1.5 md:gap-4 mb-3 md:mb-8" data-id="element-268">
            {/* Card 1: Kit Economía - $19.990 */}
            <button onClick={() => setSelectedPack(0)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 0 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-269">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 0 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-270">
                {selectedPack === 0 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-271" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-272">📚</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-273">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-274">Economía Lista</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-275">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-276">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-277" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-278">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-279" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-280">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-281" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-282">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-283">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-284">
                $39.990
              </span>
            </button>

            {/* Card 2: Kit Contabilidad - $19.990 */}
            <button onClick={() => setSelectedPack(2)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 2 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-285">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 2 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-286">
                {selectedPack === 2 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-287" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-288">🧮</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-289">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-290">Contabilidad Lista</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-291">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-292">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-293" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-294">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-295" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-296">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-297" />
                  3 bonus adaptados a Contabilidad
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-298">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-299">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-300">
                $39.990
              </span>
            </button>

            {/* Card 3: Kit Proyecto Organizacional - $19.990 */}
            <button onClick={() => setSelectedPack(4)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 4 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-301">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 4 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-302">
                {selectedPack === 4 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-303" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-304">📋</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-305">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-306">Proyecto Organizacional</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-307">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-308">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-309" />
                  6 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-310">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-311" />
                  2 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-312">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-313">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-314">
                $39.990
              </span>
            </button>

            {/* Card 4: Kit Teoría de las Organizaciones - $19.990 */}
            <button onClick={() => setSelectedPack(5)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 5 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-315">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 5 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-316">
                {selectedPack === 5 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-317" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-318">🏛️</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-319">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-320">
                  Teoría de las Organizaciones
                </span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-321">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-322">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-323" />
                  6 tomos de 60 páginas c/u
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-324">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-325" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-326">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-327">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-328">
                $39.990
              </span>
            </button>

            {/* Card 5: Sistema PRO + Kit Contabilidad - $27.990 */}
            <button onClick={() => setSelectedPack(1)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 1 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-329">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#22c55e] text-white text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-330">
                ⭐ Más elegido
              </span>
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 1 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-331">
                {selectedPack === 1 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-332" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-333">👑</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-334">
                <span className="text-[#eab308]" data-id="element-335">Sistema PRO</span> de
                Evaluación Contable +{' '}
                <span className="text-[#eab308]" data-id="element-336">Kit de Contabilidad</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-337">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-338">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-339" />
                  Kit Completo de Contabilidad incluido
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-340">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-341" />
                  8 tomos de Contabilidad completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-342">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-343" />
                  Sistema PRO de Evaluación Contable
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-344">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-345" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-346">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-347">
                  $27.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-348">
                $49.990
              </span>
            </button>

            {/* Card 6: Contabilidad + Teoría de las Organizaciones - $37.990 */}
            <button onClick={() => setSelectedPack(6)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 6 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-349">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-350">
                🔥 Combo
              </span>
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 6 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-351">
                {selectedPack === 6 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-352" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-353">📦</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-354">
                Contabilidad +{' '}
                <span className="text-[#eab308]" data-id="element-355">Teoría de las Org.</span>
              </h3>
              <p className="text-gray-500 text-[10px] md:text-sm mb-1.5" data-id="element-356">
                Kit Completo Contabilidad + Teoría de las Organizaciones
              </p>
              <ul className="space-y-0.5 mb-1.5" data-id="element-357">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-358">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-359" />
                  10 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-360">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-361" />
                  7 bonus incluidos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-362">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-363" />
                  Todo incluido
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-364">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-365">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-366">
                $59.990
              </span>
            </button>

            {/* Card 7: Pack Premium - $37.990 */}
            <button onClick={() => setSelectedPack(3)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 3 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-367">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-368">
                🏆 Pack Premium
              </span>
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 3 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-369">
                {selectedPack === 3 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-370" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-371">💎</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-372">
                Pack Premium Completo
              </h3>
              <p className="text-gray-500 text-[10px] md:text-sm mb-1.5" data-id="element-373">
                <span className="text-[#eab308] font-bold" data-id="element-374">
                  Kit Completo Economía
                </span>{' '}
                +{' '}
                <span className="text-[#eab308] font-bold" data-id="element-375">
                  Kit Completo Contabilidad
                </span>
              </p>
              <ul className="space-y-0.5 mb-1.5" data-id="element-376">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-377">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-378" />
                  8 tomos teoría (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-379">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-380" />
                  8 tomos actividades (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-381">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-382" />
                  Todos los bonus de ambos kits
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-383">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-384">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-385">
                $69.990
              </span>
            </button>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-3 md:mb-6" data-id="element-386">
            <a href={packLinks[selectedPack]} className="inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-lg md:text-2xl py-3 px-6 md:py-5 md:px-16 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] transform hover:scale-[1.02] transition-all duration-200" data-id="element-387">
              Sí, quiero mi pack →
            </a>
            <p className="text-gray-500 text-xs md:text-sm font-medium mt-2 flex items-center justify-center gap-1.5" data-id="element-388">
              <Lock className="w-3.5 h-3.5" data-id="element-389" /> PAGO SEGURO CON MERCADO PAGO
            </p>
          </div>

          {/* Features Box */}
          <div className="max-w-2xl mx-auto bg-[#111633] border border-[#1e2a5a] rounded-xl p-3 md:p-8" data-id="element-390">
            <h3 className="text-white font-black text-base md:text-lg mb-2 md:mb-5 flex items-center gap-2" data-id="element-391">
              👑 PACK COMPLETO INCLUYE:
            </h3>
            <ul className="space-y-1.5 md:space-y-3" data-id="element-392">
              {['Kit completo de contenidos para todo el año', 'Maletín de actividades listas para el aula', 'BONUS: Guía docente de primeros auxilios', 'BONUS: Guía maestra de planificación anual', 'BONUS: Actividades para tus clases', 'Entrega inmediata por Drive (PDF)', 'Acceso de por vida — sin vencimiento', 'Garantía de satisfacción de 7 días'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm md:text-base" data-id="element-393">
                  <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-394" />
                  {item}
                </li>)}
            </ul>
          </div>

          {/* Payment methods */}
          <p className="text-center text-gray-600 text-xs md:text-sm mt-3 md:mt-6 flex items-center justify-center gap-2" data-id="element-395">
            <CreditCard className="w-4 h-4" data-id="element-396" />
            Transferencia · Mercado Pago · Tarjeta
          </p>
        </div>
      </section>

      {/* SECTION 10: HOW IT WORKS */}
      <section className="bg-white py-4 md:py-12 px-4" data-id="element-397">
        <div className="max-w-3xl mx-auto text-center" data-id="element-398">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-8" data-id="element-399">
            🔽 ¿Cómo recibís el{' '}
            <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-4" data-id="element-400">
              material
            </span>
            ?
          </h2>

          <div className="grid grid-cols-3 gap-2 md:gap-6" data-id="element-401">
            <div className="relative" data-id="element-402">
              <div className="w-9 h-9 md:w-14 md:h-14 bg-gray-100 rounded-full flex items-center justify-center text-base md:text-2xl font-bold text-gray-900 mx-auto mb-1 md:mb-3 border-2 border-gray-200" data-id="element-403">
                1
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2" data-id="element-404">
                Comprás
              </h3>
              <p className="text-gray-600 text-[11px] md:text-base leading-tight" data-id="element-405">
                Realizás la compra e ingresás tu correo.
              </p>
            </div>
            <div className="relative" data-id="element-406">
              <div className="w-9 h-9 md:w-14 md:h-14 bg-gray-100 rounded-full flex items-center justify-center text-base md:text-2xl font-bold text-gray-900 mx-auto mb-1 md:mb-3 border-2 border-gray-200" data-id="element-407">
                2
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2" data-id="element-408">
                Pagás
              </h3>
              <p className="text-gray-600 text-[11px] md:text-base leading-tight" data-id="element-409">
                Finalizás el pago de forma segura.
              </p>
            </div>
            <div className="relative" data-id="element-410">
              <div className="w-9 h-9 md:w-14 md:h-14 bg-gray-100 rounded-full flex items-center justify-center text-base md:text-2xl font-bold text-gray-900 mx-auto mb-1 md:mb-3 border-2 border-gray-200" data-id="element-411">
                3
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2" data-id="element-412">
                Descargás
              </h3>
              <p className="text-gray-600 text-[11px] md:text-base leading-tight" data-id="element-413">
                Recibís el link de descarga en tu correo.
              </p>
            </div>
          </div>

          <div className="mt-3 md:mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-2 md:p-4 inline-block" data-id="element-414">
            <p className="text-yellow-800 flex items-center justify-center gap-1.5 font-medium text-xs md:text-base" data-id="element-415">
              <AlertTriangle className="w-3.5 h-3.5 md:w-5 md:h-5" data-id="element-416" />
              Si no lo ves en tu bandeja, revisá spam.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 12: GUARANTEE */}
      <section className="bg-white py-4 md:py-16 px-3 border-t border-gray-100" data-id="element-417">
        <div className="max-w-3xl mx-auto text-center" data-id="element-418">
          <ShieldCheck className="w-12 h-12 md:w-20 md:h-20 text-[#eab308] mx-auto mb-2 md:mb-6" data-id="element-419" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2 md:mb-4" data-id="element-420">
            Garantía de{' '}
            <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-4" data-id="element-421">
              Satisfacción Total
            </span>
          </h2>
          <p className="text-xl text-gray-600" data-id="element-422">
            Probalo 7 días. Si no cumple tus expectativas, te devolvemos el
            100%. Sin preguntas.
          </p>
        </div>
      </section>

      {/* SECTION 13: TESTIMONIALS */}
      <section className="bg-gray-50 py-4 md:py-20 px-3" data-id="element-423">
        <div className="max-w-5xl mx-auto" data-id="element-424">
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 mb-3 md:mb-12" data-id="element-425">
            <span className="text-[#eab308]" data-id="element-426">Testimonios</span> de Docentes en
            Argentina
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-6" data-id="element-427">
            {[{
            text: 'Ahora organizo todo el año de Contabilidad sin estrés. Es increíble el tiempo que ahorro.',
            author: 'Natalia G.',
            loc: 'Córdoba'
          }, {
            text: 'Mis estudiantes entienden los asientos contables y el patrimonio con ejemplos reales y prácticos.',
            author: 'Martín R.',
            loc: 'Buenos Aires'
          }, {
            text: 'Las clases de registración contable cambiaron por completo. Todo está más claro y estructurado.',
            author: 'Laura P.',
            loc: 'Mendoza'
          }, {
            text: 'Antes tardaba horas armando ejercicios de Libro Diario y Mayor. Ahora tengo todo listo.',
            author: 'Cecilia M.',
            loc: 'Rosario'
          }, {
            text: 'Los ejercicios integradores de estados contables hacen que participen todos.',
            author: 'Diego F.',
            loc: 'Tucumán'
          }].map((t, i) => <div key={i} className="bg-white p-3 md:p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-428">
                <div className="flex gap-1 text-yellow-400 mb-2 md:mb-4" data-id="element-429">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-current" data-id="element-430" />)}
                </div>
                <p className="text-gray-700 mb-2 md:mb-4 italic" data-id="element-431">"{t.text}"</p>
                <div className="font-bold text-gray-900 text-sm" data-id="element-432">
                  {t.author} –{' '}
                  <span className="text-gray-500 font-normal" data-id="element-433">{t.loc}</span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 14: FAQ */}
      <section className="bg-white py-4 md:py-16 px-3" data-id="element-434">
        <div className="max-w-3xl mx-auto" data-id="element-435">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-3 md:mb-12" data-id="element-436">
            Preguntas{' '}
            <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-4" data-id="element-437">
              Frecuentes
            </span>
          </h2>

          <div className="space-y-1.5 md:space-y-4" data-id="element-438">
            {[{
            q: '¿En qué formato recibo el material?',
            a: 'Recibís todo en formato PDF, listo para descargar e imprimir o usar en formato digital.'
          }, {
            q: '¿Puedo imprimir el material?',
            a: 'Sí, podés imprimir todo el material las veces que necesites.'
          }, {
            q: '¿Para qué año sirve?',
            a: 'El kit está diseñado para todos los años de secundaria donde se dicte Contabilidad.'
          }, {
            q: '¿Cubre todos los temas del programa de Contabilidad?',
            a: 'Sí, incluye desde los fundamentos (patrimonio, ecuación patrimonial) hasta registración contable, estados contables y educación financiera.'
          }, {
            q: '¿Necesito materiales especiales?',
            a: 'No, solo necesitás una computadora o celular para descargar el material.'
          }, {
            q: '¿Cuánto tarda en llegar?',
            a: 'El acceso es inmediato. Apenas completás el pago, recibís el link de descarga en tu correo.'
          }].map((faq, i) => <details key={i} className="group bg-gray-50 rounded-lg border border-gray-200 open:bg-white open:shadow-md transition-all duration-200" data-id="element-439">
                <summary className="flex items-center justify-between cursor-pointer p-3 md:p-6 font-bold text-gray-900 list-none" data-id="element-440">
                  {faq.q}
                  <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" data-id="element-441" />
                </summary>
                <div className="px-3 pb-3 md:px-6 md:pb-6 text-gray-600 leading-relaxed border-t border-gray-100 pt-4" data-id="element-442">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* SECTION 15: FINAL CTA */}
      <section className="bg-black text-white py-4 md:py-20 px-3 text-center" data-id="element-443">
        <div className="max-w-4xl mx-auto" data-id="element-444">
          <h2 className="text-3xl md:text-5xl font-black mb-2 md:mb-4" data-id="element-445">
            ¿Listo/a para{' '}
            <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-8" data-id="element-446">
              transformar tu año
            </span>{' '}
            en Contabilidad?
          </h2>
          <p className="text-xl text-gray-400 mb-3 md:mb-10" data-id="element-447">
            Sumate a docentes que ya dejaron de improvisar.
          </p>

          <p className="text-gray-500 font-bold uppercase tracking-widest text-sm" data-id="element-448">
            📈 KIT COMPLETO CONTABILIDAD LISTA
          </p>
        </div>
      </section>

      {/* SECTION 15.5: BOTTOM PRICING (duplicate) */}
      <section className="py-4 md:py-20 px-3" style={{
      background: 'linear-gradient(180deg, #080c1a 0%, #0d1229 50%, #080c1a 100%)'
    }} data-id="element-449">
        <div className="max-w-5xl mx-auto" data-id="element-450">
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-3 md:mb-12 italic" data-id="element-451">
            Elegí tu{' '}
            <span className="text-[#eab308] underline decoration-[#eab308]/30 decoration-4 underline-offset-8" data-id="element-452">
              Pack Completo
            </span>
          </h2>

          <div className="grid grid-cols-2 gap-1.5 md:gap-4 mb-3 md:mb-8" data-id="element-453">
            {/* Card 1: Kit Economía - $19.990 */}
            <button onClick={() => setSelectedPack(0)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 0 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-454">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 0 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-455">
                {selectedPack === 0 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-456" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-457">📚</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-458">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-459">Economía Lista</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-460">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-461">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-462" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-463">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-464" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-465">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-466" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-467">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-468">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-469">
                $39.990
              </span>
            </button>

            {/* Card 2: Kit Contabilidad - $19.990 */}
            <button onClick={() => setSelectedPack(2)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 2 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-470">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 2 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-471">
                {selectedPack === 2 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-472" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-473">🧮</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-474">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-475">Contabilidad Lista</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-476">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-477">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-478" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-479">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-480" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-481">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-482" />
                  3 bonus adaptados a Contabilidad
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-483">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-484">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-485">
                $39.990
              </span>
            </button>

            {/* Card 3: Kit Proyecto Organizacional - $19.990 */}
            <button onClick={() => setSelectedPack(4)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 4 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-486">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 4 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-487">
                {selectedPack === 4 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-488" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-489">📋</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-490">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-491">Proyecto Organizacional</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-492">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-493">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-494" />
                  6 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-495">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-496" />
                  2 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-497">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-498">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-499">
                $39.990
              </span>
            </button>

            {/* Card 4: Kit Teoría de las Organizaciones - $19.990 */}
            <button onClick={() => setSelectedPack(5)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 5 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-500">
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 5 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-501">
                {selectedPack === 5 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-502" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-503">🏛️</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-504">
                Kit Completo{' '}
                <span className="text-[#eab308]" data-id="element-505">
                  Teoría de las Organizaciones
                </span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-506">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-507">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-508" />
                  6 tomos de 60 páginas c/u
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-509">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-510" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-511">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-512">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-513">
                $39.990
              </span>
            </button>

            {/* Card 5: Sistema PRO + Kit Contabilidad - $27.990 */}
            <button onClick={() => setSelectedPack(1)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 1 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-514">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#22c55e] text-white text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-515">
                ⭐ Más elegido
              </span>
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 1 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-516">
                {selectedPack === 1 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-517" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-518">👑</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-519">
                <span className="text-[#eab308]" data-id="element-520">Sistema PRO</span> de
                Evaluación Contable +{' '}
                <span className="text-[#eab308]" data-id="element-521">Kit de Contabilidad</span>
              </h3>
              <ul className="space-y-0.5 mb-1.5" data-id="element-522">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-523">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-524" />
                  Kit Completo de Contabilidad incluido
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-525">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-526" />
                  8 tomos de Contabilidad completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-527">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-528" />
                  Sistema PRO de Evaluación Contable
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-529">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-530" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-531">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-532">
                  $27.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-533">
                $49.990
              </span>
            </button>

            {/* Card 6: Contabilidad + Teoría de las Organizaciones - $37.990 */}
            <button onClick={() => setSelectedPack(6)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 6 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-534">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-535">
                🔥 Combo
              </span>
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 6 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-536">
                {selectedPack === 6 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-537" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-538">📦</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-539">
                Contabilidad +{' '}
                <span className="text-[#eab308]" data-id="element-540">Teoría de las Org.</span>
              </h3>
              <p className="text-gray-500 text-[10px] md:text-sm mb-1.5" data-id="element-541">
                Kit Completo Contabilidad + Teoría de las Organizaciones
              </p>
              <ul className="space-y-0.5 mb-1.5" data-id="element-542">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-543">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-544" />
                  10 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-545">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-546" />
                  7 bonus incluidos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-547">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-548" />
                  Todo incluido
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-549">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-550">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-551">
                $59.990
              </span>
            </button>

            {/* Card 7: Pack Premium - $37.990 */}
            <button onClick={() => setSelectedPack(3)} className={`relative text-left rounded-lg p-2 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 3 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-552">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-553">
                🏆 Pack Premium
              </span>
              <div className={`absolute top-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 3 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-554">
                {selectedPack === 3 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-555" />}
              </div>
              <div className="text-lg mb-0.5" data-id="element-556">💎</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-557">
                Pack Premium Completo
              </h3>
              <p className="text-gray-500 text-[10px] md:text-sm mb-1.5" data-id="element-558">
                <span className="text-[#eab308] font-bold" data-id="element-559">
                  Kit Completo Economía
                </span>{' '}
                +{' '}
                <span className="text-[#eab308] font-bold" data-id="element-560">
                  Kit Completo Contabilidad
                </span>
              </p>
              <ul className="space-y-0.5 mb-1.5" data-id="element-561">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-562">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-563" />
                  8 tomos teoría (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-564">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-565" />
                  8 tomos actividades (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-566">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-567" />
                  Todos los bonus de ambos kits
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-568">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-569">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-570">
                $69.990
              </span>
            </button>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-3 md:mb-6" data-id="element-571">
            <a href={packLinks[selectedPack]} className="inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-lg md:text-2xl py-3 px-6 md:py-5 md:px-16 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] transform hover:scale-[1.02] transition-all duration-200" data-id="element-572">
              Sí, quiero mi pack →
            </a>
            <p className="text-gray-500 text-xs md:text-sm font-medium mt-2 flex items-center justify-center gap-1.5" data-id="element-573">
              <Lock className="w-3.5 h-3.5" data-id="element-574" /> PAGO SEGURO CON MERCADO PAGO
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 16: FOOTER */}
      <footer className="bg-gray-900 text-gray-500 py-3 md:py-8 px-3 text-center text-sm border-t border-gray-800" data-id="element-575">
        <p className="mb-2 md:mb-4" data-id="element-576">
          © 2026 Kit Completo Contabilidad Lista™
        </p>
        <div className="flex justify-center gap-4" data-id="element-577">
          <span className="hover:text-white cursor-pointer" data-id="element-578">Términos</span>
          <span className="hover:text-white cursor-pointer" data-id="element-579">Privacidad</span>
          <span className="hover:text-white cursor-pointer" data-id="element-580">Soporte</span>
        </div>
      </footer>
    </div>;
}